import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Descriptions,
  Tag,
  Spin,
  Alert,
  Typography,
  Timeline,
  Card,
  Space,
  Button,
  message,
  Tooltip,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  BankOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminTransferEndpoints, type TransferDetail, type TransferEvent } from '../api/adminTransferEndpoints';
import RefChip from './RefChip';
import EntityStatusTag from './EntityStatusTag';

const { Text } = Typography;

interface TransferDetailDrawerProps {
  transferId: string | null;
  open: boolean;
  onClose: () => void;
  onWithdrawalClick?: (withdrawalId: string) => void;
  zIndex?: number;
}

const TransferDetailDrawer: React.FC<TransferDetailDrawerProps> = ({
  transferId,
  open,
  onClose,
  onWithdrawalClick,
  zIndex = 1001,
}) => {
  const [loading, setLoading] = useState(false);
  const [transfer, setTransfer] = useState<TransferDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [applyingLedger, setApplyingLedger] = useState(false);

  useEffect(() => {
    if (open && transferId) {
      fetchTransferDetail();
    }
  }, [open, transferId]);

  const fetchTransferDetail = async () => {
    if (!transferId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await adminTransferEndpoints.getDetail(transferId);
      setTransfer(data);
    } catch (err: any) {
      console.error('Failed to fetch transfer details:', err);
      setError(err.response?.data?.error || 'Failed to load transfer details');
    } finally {
      setLoading(false);
    }
  };

  // S-H3: Refresh transfer status from provider
  const handleRefreshStatus = async () => {
    if (!transferId) return;

    try {
      setRefreshing(true);
      const response = await adminTransferEndpoints.refreshStatus(transferId);
      message.success(response.message || 'Transfer status refreshed');
      // Refetch to get updated data
      await fetchTransferDetail();
    } catch (err: any) {
      console.error('Failed to refresh transfer status:', err);
      message.error(err.response?.data?.error || 'Failed to refresh transfer status');
    } finally {
      setRefreshing(false);
    }
  };

  // S-H3: Apply ledger mapping (idempotent)
  const handleApplyLedgerMapping = async () => {
    if (!transferId) return;

    try {
      setApplyingLedger(true);
      const response = await adminTransferEndpoints.applyLedgerMapping(transferId);
      if (response.entry_created) {
        message.success('Ledger entry created successfully');
      } else {
        message.info('Ledger entry already exists (idempotent)');
      }
      // Refetch to get updated data
      await fetchTransferDetail();
    } catch (err: any) {
      console.error('Failed to apply ledger mapping:', err);
      message.error(err.response?.data?.error || 'Failed to apply ledger mapping');
    } finally {
      setApplyingLedger(false);
    }
  };

  // S-H3: Copy to clipboard helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM D, YYYY h:mm:ss A');
  };

  const getTransferTypeIcon = (type: string) => {
    return type === 'deposit' ? <ArrowDownOutlined /> : <ArrowUpOutlined />;
  };

  return (
    <Drawer
      title={
        transfer ? (
          <Space>
            <span>Transfer • {transfer.id.slice(-8)}</span>
            {getTransferTypeIcon(transfer.transfer_type)}
            <EntityStatusTag kind="transfer" status={transfer.status} size="small" />
          </Space>
        ) : (
          <span>Transfer Details</span>
        )
      }
      placement="right"
      width={720}
      onClose={onClose}
      open={open}
      zIndex={zIndex}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchTransferDetail}
          loading={loading}
        >
          Refresh
        </Button>
      }
    >
      {loading && !transfer && (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading transfer details...</Text>
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message="Error Loading Transfer"
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {transfer && !loading && (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* S-H3: Action Buttons */}
          <Card size="small">
            <Space wrap>
              <Tooltip title="Refresh status from provider">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshStatus}
                  loading={refreshing}
                  disabled={refreshing}
                >
                  Refresh Status
                </Button>
              </Tooltip>
              
              {transfer.status === 'succeeded' && transfer.transfer_type === 'deposit' && (
                <Tooltip title="Create company ledger entry (idempotent)">
                  <Button
                    icon={<BankOutlined />}
                    onClick={handleApplyLedgerMapping}
                    loading={applyingLedger}
                    disabled={applyingLedger}
                  >
                    Apply Ledger Mapping
                  </Button>
                </Tooltip>
              )}

              <Tooltip title="Copy transfer ID">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(transfer.id, 'Transfer ID')}
                >
                  Copy ID
                </Button>
              </Tooltip>

              {transfer.provider_transfer_id && (
                <Tooltip title="Copy provider transfer ID">
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(transfer.provider_transfer_id!, 'Provider Transfer ID')}
                  >
                    Copy Provider ID
                  </Button>
                </Tooltip>
              )}

              {transfer.withdrawal_request_id && onWithdrawalClick && (
                <Tooltip title="View withdrawal request">
                  <Button
                    icon={<LinkOutlined />}
                    onClick={() => onWithdrawalClick(transfer.withdrawal_request_id!)}
                  >
                    View Request
                  </Button>
                </Tooltip>
              )}
            </Space>
          </Card>

          {/* Stale Warning */}
          {transfer.stale_flag && (
            <Alert
              type="warning"
              message="Stale Transfer"
              description={
                <div>
                  <div>This transfer has been flagged as stale.</div>
                  {transfer.stale_reason && <div><strong>Reason:</strong> {transfer.stale_reason}</div>}
                  {transfer.last_checked_at && (
                    <div><strong>Last checked:</strong> {formatDateTime(transfer.last_checked_at)}</div>
                  )}
                </div>
              }
              showIcon
            />
          )}

          {/* Basic Information */}
          <Card title="Basic Information" size="small">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Transfer ID">
                <RefChip value={transfer.id} label="Transfer" />
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color={transfer.transfer_type === 'deposit' ? 'green' : 'blue'}>
                  {transfer.transfer_type.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong style={{ fontSize: 16 }}>
                  {formatCurrency(transfer.amount_cents)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <EntityStatusTag kind="transfer" status={transfer.status} />
              </Descriptions.Item>
              {transfer.failure_reason && (
                <Descriptions.Item label="Failure Reason">
                  <Text type="danger">{transfer.failure_reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Provider Information */}
          <Card title="Provider Information" size="small">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Provider">
                <Tag>{transfer.provider.toUpperCase()}</Tag>
              </Descriptions.Item>
              {transfer.provider_transfer_id && (
                <Descriptions.Item label="Provider Transfer ID">
                  <RefChip
                    value={transfer.provider_transfer_id}
                    label="Plaid"
                    tooltip="Provider transfer ID (copy-only)"
                  />
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Rail">
                {transfer.rail.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Currency">
                {transfer.currency}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* User Information */}
          {transfer.owner && (
            <Card title="User Information" size="small">
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Name">
                  {transfer.owner.name}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {transfer.owner.email}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Bank Account Information */}
          {transfer.bank_account && (
            <Card title="Bank Account" size="small">
              <Descriptions column={1} bordered size="small">
                {transfer.bank_account.institution_name && (
                  <Descriptions.Item label="Institution">
                    {transfer.bank_account.institution_name}
                  </Descriptions.Item>
                )}
                {transfer.bank_account.account_name && (
                  <Descriptions.Item label="Account">
                    {transfer.bank_account.account_name}
                  </Descriptions.Item>
                )}
                {transfer.bank_account.account_type && (
                  <Descriptions.Item label="Type">
                    {transfer.bank_account.account_type}
                  </Descriptions.Item>
                )}
                {transfer.bank_account.mask && (
                  <Descriptions.Item label="Last 4">
                    ****{transfer.bank_account.mask}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Timestamps */}
          <Card title="Timeline" size="small">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Created">
                {formatDateTime(transfer.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {formatDateTime(transfer.updated_at)}
              </Descriptions.Item>
              {transfer.completed_at && (
                <Descriptions.Item label="Completed">
                  {formatDateTime(transfer.completed_at)}
                </Descriptions.Item>
              )}
              {transfer.last_checked_at && (
                <Descriptions.Item label="Last Checked">
                  {formatDateTime(transfer.last_checked_at)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Events Timeline */}
          {transfer.events && transfer.events.length > 0 && (
            <Card title="Activity Timeline" size="small">
              <Timeline
                items={transfer.events.map((event: TransferEvent) => ({
                  color: event.event_type.includes('completed') || event.event_type.includes('succeeded')
                    ? 'green'
                    : event.event_type.includes('failed')
                    ? 'red'
                    : 'blue',
                  children: (
                    <div>
                      <div><Text strong>{event.event_type.replace(/_/g, ' ').toUpperCase()}</Text></div>
                      <div><Text type="secondary" style={{ fontSize: 12 }}>
                        {formatDateTime(event.occurred_at)}
                      </Text></div>
                      {event.status_from && event.status_to && (
                        <div><Text type="secondary" style={{ fontSize: 12 }}>
                          {event.status_from} → {event.status_to}
                        </Text></div>
                      )}
                      {event.actor_type && (
                        <div><Text type="secondary" style={{ fontSize: 12 }}>
                          Actor: {event.actor_type}
                        </Text></div>
                      )}
                    </div>
                  ),
                }))}
              />
            </Card>
          )}

          {/* Linked Entities */}
          {(transfer.withdrawal_request_id || transfer.metadata) && (
            <Card title="Linked Entities" size="small">
              <Descriptions column={1} bordered size="small">
                {transfer.withdrawal_request_id && (
                  <Descriptions.Item label="Withdrawal Request">
                    <RefChip
                      value={transfer.withdrawal_request_id}
                      label="Request"
                      onOpen={onWithdrawalClick ? () => onWithdrawalClick(transfer.withdrawal_request_id!) : undefined}
                    />
                  </Descriptions.Item>
                )}
                {transfer.metadata && Object.keys(transfer.metadata).length > 0 && (
                  <Descriptions.Item label="Metadata">
                    <pre style={{ fontSize: 11, marginTop: 8, padding: 8, borderRadius: 4 }}>
                      {JSON.stringify(transfer.metadata, null, 2)}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Memo */}
          {transfer.memo && (
            <Card title="Memo" size="small">
              <Text>{transfer.memo}</Text>
            </Card>
          )}
        </Space>
      )}
    </Drawer>
  );
};

export default TransferDetailDrawer;
